import cogoToast from "cogo-toast";
import React, { useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Button, Col, Form as FormLayout, Modal, Nav, Row, Tab, Table } from "react-bootstrap";
import { ViewIcon } from "../../../util/Icons";
import TroveNextTransitionModal from "./modal/TroveNextTransitionModal";
import { DocumentTab } from "./modal/sections/documents";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import MagnifiableImage from "../../Common/components/MagnifiableImage";
import TextInput from "../../Common/components/text-input";
import SelectInput from "../../Common/components/select-input";
import { FilterGroup } from "../../Common/components/FilterGroup";
import { errorMessage, formatStatus } from "../../../util/Helpers";
import {
    useActivateTroveNextProfileMutation,
    useApproveTroveNextProfileDocumentMutation,
    useApproveTroveNextProfileTransitionMutation,
    useGetTroveNextProfileDocumentsQuery,
    useGetTroveNextProfilesQuery,
    useGetTroveNextProfileTransitionStatusQuery,
    useRejectTroveNextProfileDocumentMutation,
    useResetTroveNextProfileDocumentMutation,
    useUpdateTroveNextProfileMutation,
} from "../../../../redux/apiSlice";

// Trove Next has no published relationship enum anywhere in either codebase -
// "PARENT" and "SON" are the only confirmed real values seen so far (the PUT
// spec example and a live list sample, respectively). The rest of this list
// is a best guess pending confirmation - flagged to the team, not verified.
const TROVE_NEXT_RELATIONSHIP_OPTIONS = ["PARENT", "GUARDIAN", "SON", "DAUGHTER", "SIBLING", "OTHER"];
const TROVE_NEXT_GENDER_OPTIONS = ["MALE", "FEMALE"];

const prettyEnum = (value) => `${value ?? ""}`.replace(/_/g, " ").trim();
const normalizeStatus = (value) => `${value ?? ""}`.trim().toUpperCase().replace(/\s+/g, "_");
// The profiles list sends single-letter gender codes ("M"/"F"); the PUT spec
// expects the full word ("MALE"/"FEMALE"). Normalize whichever shape shows up
// on read so the edit form's select always matches one of its own options.
const normalizeGenderForEdit = (value) => {
    const normalized = `${value ?? ""}`.trim().toUpperCase();
    if (normalized === "M") return "MALE";
    if (normalized === "F") return "FEMALE";
    if (normalized === "MALE" || normalized === "FEMALE") return normalized;
    return "";
};
const normalizeIdType = (value) =>
    `${value ?? ""}`
        .trim()
        .toUpperCase()
        .replace(/[\s-]+/g, "_");
const isCompletedStatus = (value) => normalizeStatus(value) === "COMPLETED";
const isBirthCertificateType = (value) => normalizeStatus(value) === "BIRTH_CERTIFICATE";
const isPendingStatus = (value) => normalizeStatus(value) === "PENDING";
const isPendingTransitionAdminStatus = (value) => normalizeStatus(value) === "PENDING_TRANSITION_ADMIN";
const renderTroveNextStatus = (value) => {
    const raw = value ?? "-";
    const normalized = normalizeStatus(raw);
    if (normalized === "TRANSITIONED") {
        return (
            <span className="label label-lg label-light-success label-inline">
                TRANSITIONED
            </span>
        );
    }
    if (normalized === "PENDING_TRANSITION_KYC") {
        return (
            <span className="label label-lg label-light-warning label-inline">
                PENDING TRANSITION KYC
            </span>
        );
    }
    if (normalized === "PENDING_TRANSITION_ADMIN") {
        return (
            <span
                className="label label-lg label-light-warning label-inline"
                style={{ color: "#FFA800" }}
            >
                PENDING TRANSITION ADMIN
            </span>
        );
    }
    return formatStatus(raw) || asText(raw);
};

const isTroveNextRecord = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;

    return [
        "profileId",
        "profile_id",
        "documentId",
        "document_id",
        "userGroup",
        "user_group",
        "documentType",
        "category",
        "type",
    ].some((key) => Object.prototype.hasOwnProperty.call(value, key));
};

const normalizeList = (value) => {
    if (Array.isArray(value)) return value;

    const candidates = [
        value?.data,
        value?.profiles,
        value?.documents,
        value?.items,
        value?.results,
        value?.rows,
        value?.data?.profiles,
        value?.data?.documents,
        value?.data?.items,
        value?.data?.results,
        value?.data?.rows,
        value?.data?.data,
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
    }

    for (const candidate of candidates) {
        if (isTroveNextRecord(candidate)) return [candidate];
    }

    return isTroveNextRecord(value) ? [value] : [];
};

// The profiles endpoint now returns an exact `total` alongside `data` -
// used for real "is there a next page"/"of N records" instead of guessing
// from whether a page came back full.
const extractTotalCount = (value) => {
    const candidates = [value?.total, value?.data?.total];
    for (const candidate of candidates) {
        if (typeof candidate === "number") return candidate;
    }
    return null;
};

const getProfileId = (profile) => profile?.profileId || profile?.id || profile?.profile_id || profile?.profileID || profile?.profileID;
const getUserId = (profile) => profile?.userId || profile?.user_id || profile?.userID || profile?.meta?.userId || profile?.meta?.user_id;
const getCustodianUserId = (profile) => profile?.custodianUserId || profile?.custodianUserID || profile?.custodian_user_id;
const getDocumentId = (doc) => doc?.documentId || doc?.id || doc?.document_id || doc?.documentID;
const getProfileName = (profile) =>
    profile?.username
    || profile?.name
    || [profile?.firstName, profile?.lastName].filter(Boolean).join(" ")
    || profile?.fullName
    || "-";
const getProfileEmail = (profile) => profile?.email || profile?.guardianEmail || profile?.parentEmail || "-";
const getProfilePhone = (profile) => profile?.phone || profile?.phoneNumber || profile?.mobile || "-";
const getCustodianName = (profile) =>
    [profile?.custodianFirstName, profile?.custodianLastName].filter(Boolean).join(" ")
    || profile?.custodianName
    || profile?.custodianFullName
    || profile?.custodian
    || "-";
const normalizeUserGroup = (value) => `${value ?? ""}`.trim().toLowerCase();

const getTroveNextProfileStatus = (profile) => (
    profile?.transitionStatus
    || profile?.transition_state
    || profile?.progressStatus
    || profile?.result
    || profile?.meta?.transitionStatus
    || profile?.meta?.transition_state
    || profile?.meta?.progressStatus
    || profile?.meta?.result
    || profile?.status
    || profile?.state
    || ""
);
const getTroveNextProfileStatusCandidates = (profile) => (
    [
        profile?.status,
        profile?.state,
        profile?.transitionStatus,
        profile?.transition_state,
        profile?.progressStatus,
        profile?.result,
        profile?.meta?.status,
        profile?.meta?.state,
        profile?.meta?.transitionStatus,
        profile?.meta?.transition_state,
        profile?.meta?.progressStatus,
        profile?.meta?.result,
    ]
        .map(normalizeStatus)
        .filter(Boolean)
);
const matchesTroveNextStatusFilter = (profile, statusFilter) => {
    const normalizedFilter = normalizeStatus(statusFilter);
    if (!normalizedFilter) return true;
    return getTroveNextProfileStatusCandidates(profile).includes(normalizedFilter);
};

const normalizeObject = (value) => {
    if (!value) return null;
    if (value?.data && typeof value.data === "object") return value.data;
    if (typeof value === "object") return value;
    return null;
};

const asText = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "number") return `${value}`;
    if (typeof value === "string") return value.trim() === "" ? "-" : value;
    if (Array.isArray(value)) {
        const flat = value
            .map((v) => (typeof v === "string" || typeof v === "number" ? `${v}` : null))
            .filter(Boolean);
        return flat.length ? flat.join(", ") : "-";
    }
    return "-";
};

const asYesNo = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "number") return value === 1 ? "Yes" : value === 0 ? "No" : `${value}`;
    if (typeof value === "string") {
        const v = value.trim().toLowerCase();
        if (v === "1" || v === "true" || v === "yes") return "Yes";
        if (v === "0" || v === "false" || v === "no") return "No";
        return value.trim() === "" ? "-" : value;
    }
    return asText(value);
};

const splitFullName = (value) => {
    const parts = `${value ?? ""}`.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstName: "", lastName: "" };
    if (parts.length === 1) return { firstName: parts[0], lastName: "" };
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
};

const buildTroveNextBasicDetailsInitialValues = (profile, transition, isFetchingTransitionStatus) => {
    const fallback = splitFullName(profile?.fullName || profile?.name || profile?.username);
    const firstName = asText(profile?.firstName) !== "-" ? profile?.firstName : fallback.firstName;
    const lastName = asText(profile?.lastName) !== "-" ? profile?.lastName : fallback.lastName;

    return {
        username: profile?.username || "",
        email: profile?.email || "",
        firstName: firstName || "",
        lastName: lastName || "",
        middleName: profile?.middleName || "",
        phone: profile?.phone || profile?.phoneNumber || profile?.mobile || "",
        nin: profile?.nin || "",
        custodian: getCustodianName(profile) || "",
        userGroup: profile?.userGroup || "",
        gender: normalizeGenderForEdit(profile?.gender),
        dob: profile?.dob || "",
        relationship: profile?.relationship || "",
        market: profile?.market || "",
        consentGiven: asYesNo(profile?.consentGiven),
        questionnaireCompleted: asYesNo(profile?.questionnaireCompleted),
        reconciliationActive: asYesNo(profile?.reconciliationActive),
        transitionStatus: isFetchingTransitionStatus
            ? "Loading..."
            : (transition?.status
                || transition?.state
                || transition?.transitionStatus
                || transition?.progressStatus
                || transition?.result
                || profile?.transitionStatus
                || ""),
        transitionReference: transition?.reference || "",
        transitionReason: transition?.reason || transition?.message || transition?.error || "",
        created: profile?.created || "",
        updated: profile?.updated || "",
        status: normalizeStatus(profile?.status || profile?.state || "") || "",
    };
};

// Only the fields the PUT /trove-next/profiles/:profileId endpoint accepts
// are editable; everything else on this tab (custodian, transition state,
// timestamps, ...) is server-derived/read-only and stays disabled.
const troveNextProfileEditSchema = Yup.object().shape({
    firstName: Yup.string().trim().required("First name is required"),
    lastName: Yup.string().trim().required("Last name is required"),
    dob: Yup.string().trim().required("Date of birth is required"),
    relationship: Yup.string().trim().required("Relationship is required"),
    gender: Yup.string().trim().required("Gender is required"),
    middleName: Yup.string(),
    nin: Yup.string(),
    email: Yup.string().email("Enter a valid email address"),
    phone: Yup.string(),
});

const TroveNextBasicDetailsLayout = ({ profile, transition, isFetchingTransitionStatus, onSaved }) => {
    const initialValues = useMemo(
        () => buildTroveNextBasicDetailsInitialValues(profile, transition, isFetchingTransitionStatus),
        [isFetchingTransitionStatus, profile, transition]
    );

    const [updateProfile, { isLoading: isSavingProfile }] = useUpdateTroveNextProfileMutation();

    const handleSubmit = async (values, { setSubmitting }) => {
        const profileId = getProfileId(profile);
        if (!profileId) {
            cogoToast.error("Missing profile id - cannot save.");
            setSubmitting(false);
            return;
        }

        const body = {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            dob: values.dob.trim(),
            relationship: values.relationship.trim(),
            gender: values.gender.trim(),
        };
        if (values.middleName?.trim()) body.middleName = values.middleName.trim();
        if (values.nin?.trim()) body.nin = values.nin.trim();
        if (values.email?.trim()) body.email = values.email.trim();
        if (values.phone?.trim()) body.phone = values.phone.trim();

        try {
            await updateProfile({ profileId, body }).unwrap();
            cogoToast.success("Profile updated");
            onSaved?.(body);
        } catch (e) {
            cogoToast.error(errorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={troveNextProfileEditSchema}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({ isSubmitting }) => {
                const saving = isSubmitting || isSavingProfile;
                return (
                    <Form>
                        <fieldset disabled={saving}>
                            <FormLayout.Row>
                                <TextInput label="First Name*" name="firstName" type="text" />
                                <TextInput label="Last Name*" name="lastName" type="text" />
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <TextInput label="Middle Name" name="middleName" type="text" />
                                <TextInput label="Phone" name="phone" type="text" />
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <TextInput label="Email" name="email" type="email" />
                                <TextInput label="NIN" name="nin" type="text" />
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <SelectInput label="Gender*" name="gender" options={TROVE_NEXT_GENDER_OPTIONS} />
                                <TextInput label="Date of Birth*" name="dob" type="date" />
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <SelectInput label="Relationship*" name="relationship" options={TROVE_NEXT_RELATIONSHIP_OPTIONS} />
                            </FormLayout.Row>
                            <FormLayout.Row className={"justify-content-end pr-3"}>
                                <Button type="submit" variant="primary" disabled={saving}>
                                    {saving ? "Saving..." : "Save"}
                                </Button>
                            </FormLayout.Row>
                        </fieldset>

                        <hr />

                        <fieldset disabled>
                            <FormLayout.Row>
                                <TextInput label="Username" name="username" type="text" />
                                <TextInput label="Custodian" name="custodian" type="text" />
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <TextInput label="User Group" name="userGroup" type="text" />
                                <TextInput label="Market" name="market" type="text" />
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <TextInput label="Consent Given" name="consentGiven" type="text" />
                                <TextInput label="Status" name="status" type="text" />
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <TextInput label="Transition Status" name="transitionStatus" type="text" />
                                <TextInput label="Transition Reference" name="transitionReference" type="text" />
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <TextInput label="Questionnaire Completed" name="questionnaireCompleted" type="text" />
                                <TextInput label="Reconciliation Active" name="reconciliationActive" type="text" />
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <TextInput label="Transition Reason" name="transitionReason" type="text" />
                                <TextInput label="Created" name="created" type="text" />
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <TextInput label="Updated" name="updated" type="text" />
                            </FormLayout.Row>
                        </fieldset>
                    </Form>
                );
            }}
        </Formik>
    );
};

const getDocumentImageSrc = (doc) =>
    doc?.document
    || doc?.url
    || doc?.imageUrl
    || doc?.fileUrl
    || (getDocumentId(doc) ? `/users/document/${getDocumentId(doc)}` : null);

const getDocumentType = (doc) => `${doc?.type || doc?.category || doc?.documentType || ""}`.toUpperCase();
const getNormalizedDocumentType = (doc) => {
    const type = getDocumentType(doc);
    if (type === "NIN" || type === "PASSPORT" || type === "INTERNATIONAL_PASSPORT") {
        return "PICTURE_ID";
    }
    return type;
};
const getNormalizedDocumentIdType = (doc) => {
    const explicitIdType = normalizeIdType(doc?.idType ?? doc?.id_type ?? doc?.idTypeName ?? "");
    if (explicitIdType) {
        return explicitIdType === "INTERNATIONAL_PASSPORT" ? "PASSPORT" : explicitIdType;
    }

    const type = getDocumentType(doc);
    if (type === "NIN") return "NIN";
    if (type === "PASSPORT" || type === "INTERNATIONAL_PASSPORT") return "PASSPORT";
    return "";
};

// "Picture ID" already covers NIN and passport docs elsewhere in this file
// (see getNormalizedDocumentType) - an approved passport should satisfy the
// Confirm gate exactly like an approved NIN does. NIN_NG isn't folded into
// that existing PICTURE_ID grouping, so it's checked for separately here.
const isPictureIdDocument = (doc) => (
    getNormalizedDocumentType(doc) === "PICTURE_ID" || getDocumentType(doc) === "NIN_NG"
);
const isBirthCertificateDocument = (doc) => isBirthCertificateType(getDocumentType(doc));
const isApprovedDocumentStatus = (doc) => {
    const status = (doc?.status || doc?.state || "").toLowerCase();
    return status === "approved" || status === "verified" || status === "accepted";
};

const TroveNextRejectDocument = ({ initialValues, onReject, isRejecting, isEditable, onCancel }) => {
    const validationSchema = Yup.object({
        rejectionReason: Yup.string().required("Required"),
    });

    const compiledInitialValues = {
        documentId: "",
        rejectionReason: "",
        ...initialValues,
    };

    return (
        <Formik
            validationSchema={validationSchema}
            initialValues={compiledInitialValues}
            validateOnMount={true}
            onSubmit={(values) => {
                onReject(values);
            }}
            enableReinitialize={true}
        >
            {({ isValid }) => (
                <Form className={"pt-4 border-top d-flex flex-column"} style={{ height: "50%" }}>
                    <fieldset disabled={!isEditable} className={"h-100 d-flex flex-column"}>
                        <div className={"flex-fill"}>
                            <FormLayout.Row>
                                <TextInput
                                    label="Rejection Reason*"
                                    name="rejectionReason"
                                    type="text"
                                    disabled={isRejecting}
                                />
                            </FormLayout.Row>
                        </div>
                        <FormLayout.Row className={"mt-6"}>
                            <Col>
                                <Button
                                    type="button"
                                    variant={"outline-dark"}
                                    className={"w-100"}
                                    onClick={onCancel}
                                    disabled={isRejecting}
                                >
                                    Cancel
                                </Button>
                            </Col>
                            <Col>
                                <Button
                                    type="submit"
                                    variant={"dark"}
                                    className={"w-100"}
                                    disabled={!isValid || isRejecting}
                                >
                                    Reject
                                </Button>
                            </Col>
                        </FormLayout.Row>
                    </fieldset>
                </Form>
            )}
        </Formik>
    );
};

const TroveNextEditDocument = ({
    initialValues,
    onApprove,
    isApproving,
    onReject,
    onReset,
    isResetting,
    isEditable,
}) => {
    const validationSchema = Yup.object({
        documentId: Yup.string().required("Required"),
        type: Yup.string().required("Required"),
        idType: Yup.string().when("type", {
            is: (type) => !isBirthCertificateType(type),
            then: Yup.string().required("Required"),
            otherwise: Yup.string(),
        }),
        side: Yup.string().required("Required"),
        idValue: Yup.string().when("type", {
            is: (type) => type?.startsWith("PICTURE_ID"),
            then: Yup.string().required("Required"),
        }),
    });

    const compiledInitialValues = {
        documentId: "",
        idValue: "",
        status: "",
        side: "front",
        ...initialValues,
        type: getNormalizedDocumentType(initialValues),
        idType: getNormalizedDocumentIdType(initialValues),
    };

    const bannerStyle = "danger";
    const bannerText = "Documents check failed.";
    const documentStatus = normalizeStatus(initialValues?.status || initialValues?.state);

    return (
        <Formik
            validationSchema={validationSchema}
            initialValues={compiledInitialValues}
            validateOnMount={true}
            onSubmit={(values) => onApprove(values.documentId)}
            enableReinitialize={true}
        >
            {({ isValid, values }) => {
                const showIdTypeField = !isBirthCertificateType(values?.type);
                const categoryOptions = [
                    ...[
                        "PICTURE_ID",
                        "PROOF_OF_ADDRESS",
                        "PICTURE_ID_PROOF_OF_ADDRESS",
                        "TAX",
                        "TRUST_DOCUMENTS",
                        "PASSPORT",
                    ],
                    ...(() => {
                        const selected = getDocumentType(values);
                        if (!selected) return [];
                        const base = new Set([
                            "PICTURE_ID",
                            "PROOF_OF_ADDRESS",
                            "PICTURE_ID_PROOF_OF_ADDRESS",
                            "TAX",
                            "TRUST_DOCUMENTS",
                            "PASSPORT",
                        ]);
                        return base.has(selected) ? [] : [selected];
                    })(),
                ].map((v) => ({ value: v, name: prettyEnum(v) }));

                return (
                <Form className={"pt-2 border-top d-flex flex-column"} style={{ height: "50%" }}>
                    <fieldset disabled={!isEditable} className={"h-100 d-flex flex-column"}>
                        <div className={"flex-fill"}>
                            <div
                                className={`d-flex align-items-center w-100 py-2 px-3 mb-2 rounded-sm text-${bannerStyle} bg-light-${bannerStyle}`}
                            >
                                <span className={"flex-fill font-weight-bold"}>
                                    {bannerText}
                                </span>
                            </div>
                            <FormLayout.Row>
                                <SelectInput
                                    label="Category*"
                                    name="type"
                                    options={categoryOptions}
                                />
                                {showIdTypeField && (
                                    <SelectInput
                                        label="ID Type*"
                                        name="idType"
                                        options={[
                                            ...[
                                                "NIN",
                                                "NIN_NG",
                                                "BVN",
                                                "PASSPORT",
                                                "DRIVERS_LICENSE",
                                                "VOTERS_CARD",
                                            ],
                                            ...(() => {
                                                const selected = normalizeIdType(values?.idType);
                                                if (!selected) return [];
                                                const base = new Set([
                                                    "NIN",
                                                    "NIN_NG",
                                                    "BVN",
                                                    "PASSPORT",
                                                    "DRIVERS_LICENSE",
                                                    "VOTERS_CARD",
                                                ]);
                                                return base.has(selected) ? [] : [selected];
                                            })(),
                                        ].map((v) => ({
                                            value: v,
                                            name: v.startsWith("NIN") ? v.replace(/_/g, " ") : prettyEnum(v),
                                        }))}
                                    />
                                )}
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <TextInput
                                    label={`ID Number${values?.type?.startsWith("PICTURE_ID") ? "*" : ""}`}
                                    name="idValue"
                                    type="text"
                                    disabled={isApproving || isResetting}
                                />
                                <SelectInput
                                    label="Side*"
                                    name="side"
                                    options={[
                                        { value: "front", name: "Front" },
                                        { value: "back", name: "Back" },
                                    ]}
                                />
                            </FormLayout.Row>
                            {documentStatus === "REJECTED" && (
                                <div className="text-danger mt-2">
                                    Reason for rejection: {initialValues?.rejectionReason || "-"}
                                </div>
                            )}
                        </div>

                        <FormLayout.Row className={"mt-3 position-sticky bg-white"} style={{ bottom: 0, zIndex: 2 }}>
                            {documentStatus !== "PENDING" && (
                                <Col>
                                    <Button
                                        type="button"
                                        variant={"outline-warning"}
                                        className={"w-100"}
                                        onClick={() => onReset(values)}
                                        disabled={!isEditable || isApproving || isResetting}
                                    >
                                        Reset
                                    </Button>
                                </Col>
                            )}
                            {documentStatus !== "REJECTED" && (
                                <Col>
                                    <Button
                                        type="button"
                                        // Solid fill instead of outline - an outline button next to an
                                        // already-approved document (no Approve button showing) read as
                                        // greyed-out/disabled even though it was always clickable.
                                        variant={"danger"}
                                        className={"w-100"}
                                        style={{ cursor: "pointer" }}
                                        onClick={onReject}
                                        disabled={!isEditable || isApproving || isResetting}
                                    >
                                        Reject
                                    </Button>
                                </Col>
                            )}
                            {documentStatus !== "APPROVED" && (
                                <Col>
                                    <Button
                                        type="submit"
                                        variant={"success"}
                                        className={"w-100"}
                                        disabled={!isValid || !isEditable || isApproving || isResetting}
                                    >
                                        Approve
                                    </Button>
                                </Col>
                            )}
                        </FormLayout.Row>
                    </fieldset>
                </Form>
                );
            }}
        </Formik>
    );
};

const TroveNextDocument = ({
    initialValues,
    isEditable,
    onApprove,
    isApproving,
    onReject,
    isRejecting,
    onReset,
    isResetting,
}) => {
    const [rejectAction, setRejectAction] = useState(false);
    const src = getDocumentImageSrc(initialValues);

    return (
        <div className={"d-flex flex-column h-100"}>
            <div className={"rounded-lg flex-fill bg-light-secondary mb-4"} style={{ minHeight: 0 }}>
                {src ? (
                    <MagnifiableImage height={"100%"} width={"100%"} src={src} alt={initialValues?.type ?? ""} />
                ) : (
                    <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                        No document image found
                    </div>
                )}
            </div>
            {!rejectAction ? (
                <TroveNextEditDocument
                    initialValues={initialValues}
                    onApprove={onApprove}
                    isApproving={isApproving}
                    onReject={() => setRejectAction(true)}
                    onReset={onReset}
                    isResetting={isResetting}
                    isEditable={isEditable}
                />
            ) : (
                <TroveNextRejectDocument
                    initialValues={initialValues}
                    onReject={onReject}
                    isRejecting={isRejecting}
                    isEditable={isEditable}
                    onCancel={() => setRejectAction(false)}
                />
            )}
        </div>
    );
};

// documentStatusOverride/omitStatusInQuery come straight from the active tab
// (see TroveNext.js) and are sent to Trove Next as-is: PENDING tab sends
// status=PENDING&documentStatus=PENDING, Rejected sends documentStatus=
// REJECTED alone (no status param). FilterGroup itself is still the same
// shared component PendingKYCTable.js uses, now only backing the From/To
// date range - the old "Documents: Required/None" dropdown is gone.
const TroveNextPendingKyc = ({ userGroup, statusFilter = "", filters, documentStatusOverride = "", omitStatusInQuery = false, noDocument = false, approxTotalCount = null }) => {
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [viewProfileId, setViewProfileId] = useState(null);
    const [viewProfile, setViewProfile] = useState(null);
    const [activeProfileTab, setActiveProfileTab] = useState("documents");
    const [activeDocumentKey, setActiveDocumentKey] = useState(null);
    const [profilesQuery, setProfilesQuery] = useState("");
    const [profilesPg, setProfilesPg] = useState(1);
    // The profiles endpoint now accepts a `search` param directly (case-
    // insensitive contains match across custodian email/username/first/
    // middle/last/full name and minor email/first/middle/last/full name) -
    // debounced so it doesn't fire a request on every keystroke. This
    // replaces the old two-step custodian-only lookup (resolving the typed
    // text to a custodianUserId via a separate /users?query= search), which
    // never matched on the minor's own name/email at all.
    const [searchTerm, setSearchTerm] = useState("");
    const startDate = filters?.startDate || "";
    const endDate = filters?.endDate || "";
    const [showTransitionModal, setShowTransitionModal] = useState(false);
    const [transitionProfile, setTransitionProfile] = useState(null);
    const [profileActionType, setProfileActionType] = useState("transition");
    const normalizedStatusFilter = normalizeStatus(statusFilter);
    const statusQueryValue = `${statusFilter ?? ""}`.trim();
    const profilesRowPerPage = 10;
    // Trove Next expects full ISO-8601 instants (inclusive bounds), not bare dates -
    // stretch the <input type="date"> value to the start/end of that day in UTC.
    const toStartOfDayISO = (value) => (value ? `${value}T00:00:00.000Z` : "");
    const toEndOfDayISO = (value) => (value ? `${value}T23:59:59.999Z` : "");
    // Real server-side pagination: page 5 of "Trove Pending" used to mean "slice
    // rows 41-50 out of whatever single unbounded fetch the server happened to
    // return" - if that fetch defaulted to 50 rows, anything past #50 was silently
    // invisible and never requested. Sending limit/offset makes each page its own
    // request, so there's no hidden ceiling on how many records can be reached.
    const troveNextProfilesQuery = useMemo(() => {
        const query = {
            limit: profilesRowPerPage,
            offset: (profilesPg - 1) * profilesRowPerPage,
        };
        // Send the user group server-side too, instead of relying only on
        // the client-side filteredByUserGroupProfiles pass below - this
        // scopes both the normal fetch and the backend's custodian-name
        // search fallback (which has to walk every profile for the active
        // filters) to just this group instead of every group's profiles.
        if (userGroup) query.userGroup = userGroup;
        // Rejected sends documentStatus=REJECTED alone - status is
        // intentionally left out even though statusFilter itself is still
        // "PENDING" for this tab (see omitStatusInQuery in TroveNext.js).
        if (statusQueryValue && !omitStatusInQuery) query.status = statusQueryValue;
        if (documentStatusOverride) query.documentStatus = documentStatusOverride;
        // "No Document" tab - the oversight backend filters to profiles without a document.
        if (noDocument) query.noDocument = "true";
        if (startDate) query.startDate = toStartOfDayISO(startDate);
        if (endDate) query.endDate = toEndOfDayISO(endDate);
        if (searchTerm) query.search = searchTerm;
        return { query };
    }, [userGroup, statusQueryValue, omitStatusInQuery, profilesPg, documentStatusOverride, noDocument, startDate, endDate, searchTerm]);

    const {
        data: profilesResponse,
        isFetching: isFetchingProfiles,
        error: profilesError,
        refetch: refetchProfiles,
    } = useGetTroveNextProfilesQuery(troveNextProfilesQuery);

    useEffect(() => {
        if (profilesError) cogoToast.error(errorMessage(profilesError));
    }, [profilesError]);

    const profiles = useMemo(() => normalizeList(profilesResponse), [profilesResponse]);
    // Real total now available - use it for an exact "is there a next page"
    // instead of guessing from whether this page came back full. Fall back
    // to the old heuristic if a response shape ever comes back without one.
    const totalProfilesCount = useMemo(() => extractTotalCount(profilesResponse), [profilesResponse]);
    const hasNextProfilesPage = totalProfilesCount != null
        ? profilesPg * profilesRowPerPage < totalProfilesCount
        : profiles.length === profilesRowPerPage;
    const filteredByUserGroupProfiles = useMemo(() => {
        const normalizedUserGroup = normalizeUserGroup(userGroup);
        if (!normalizedUserGroup) return profiles;

        return profiles.filter((row) => {
            const candidateGroups = [
                row?.userGroup,
                row?.userGroupId,
                row?.groupId,
                row?.group_id,
                row?.user_group,
                row?.meta?.userGroup,
                row?.meta?.userGroupId,
                row?.meta?.groupId,
                row?.meta?.group_id,
                row?.meta?.user_group,
            ]
                .map(normalizeUserGroup)
                .filter(Boolean);

            if (candidateGroups.length === 0) return true;
            return candidateGroups.includes(normalizedUserGroup);
        });
    }, [profiles, userGroup]);
    const selectedProfiles = useMemo(() => {
        if (!normalizedStatusFilter) return filteredByUserGroupProfiles;

        const statusMatchedProfiles = filteredByUserGroupProfiles.filter((row) => (
            matchesTroveNextStatusFilter(row, normalizedStatusFilter)
        ));

        // Some Trove Next status-filtered responses do not echo the requested status
        // on the returned profile fields, so trust the server-filtered result when needed.
        return statusMatchedProfiles.length > 0 ? statusMatchedProfiles : filteredByUserGroupProfiles;
    }, [filteredByUserGroupProfiles, normalizedStatusFilter]);
    // Search is now server-side (query.search, above) - the fetched page is
    // already filtered by the API, so no further client-side narrowing is
    // needed here.
    const filteredProfiles = selectedProfiles;

    useEffect(() => {
        setProfilesPg(1);
    }, [statusFilter, userGroup, documentStatusOverride, noDocument, startDate, endDate, searchTerm]);

    // Debounce the search box into `searchTerm` so it doesn't fire a request
    // on every keystroke.
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setSearchTerm(profilesQuery.trim());
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [profilesQuery]);

    const [activateProfile, { isLoading: isActivatingProfile }] = useActivateTroveNextProfileMutation();
    const [approveTransition, { isLoading: isApprovingTransition }] = useApproveTroveNextProfileTransitionMutation();

    const openProfileActionModal = (profile, actionType) => {
        setTransitionProfile(profile ?? null);
        setProfileActionType(actionType);
        setShowTransitionModal(true);
    };

    const closeTransitionModal = () => {
        setShowTransitionModal(false);
        setTransitionProfile(null);
        setProfileActionType("transition");
    };

    const confirmTransition = async () => {
        const profileId = getProfileId(transitionProfile);
        if (!profileId) return;
        try {
            if (profileActionType === "activate") {
                await activateProfile({ profileId }).unwrap();
                cogoToast.success("User confirmed");
                closeProfileModal();
            } else {
                await approveTransition({ profileId }).unwrap();
                cogoToast.success("Transition approved");
                closeTransitionModal();
            }
            refetchProfiles();
            if (showProfileModal && viewProfileId === profileId) refetchTransitionStatus();
        } catch (e) {
            cogoToast.error(errorMessage(e));
        }
    };

    const openProfileModal = (profile) => {
        const profileId = getProfileId(profile);
        setViewProfileId(profileId);
        setViewProfile(profile ?? null);
        setActiveProfileTab("documents");
        setActiveDocumentKey(null);
        setShowProfileModal(true);
    };

    const closeProfileModal = () => {
        setShowProfileModal(false);
        setViewProfileId(null);
        setViewProfile(null);
        setActiveProfileTab("documents");
        setActiveDocumentKey(null);
        closeTransitionModal();
    };

    const {
        currentData: documentsResponse,
        isFetching: isFetchingDocuments,
        refetch: refetchDocuments,
    } = useGetTroveNextProfileDocumentsQuery(
        { profileId: viewProfileId },
        { skip: !viewProfileId || !showProfileModal }
    );

    const documents = useMemo(() => normalizeList(documentsResponse), [documentsResponse]);

    const pendingDocument = useMemo(() => {
        return documents?.some((doc) => (doc?.status || doc?.state || "").toLowerCase() === "pending");
    }, [documents]);

    // Previously this only counted approved documents (>= 2, and every fetched
    // document approved) - it never checked WHICH documents were approved. Two
    // NIN uploads both approved (a real incident) satisfied that count with no
    // birth certificate in sight, so Confirm lit up incorrectly. Check the two
    // required document types explicitly instead of counting.
    const hasApprovedPictureId = useMemo(() => (
        documents.some((doc) => isPictureIdDocument(doc) && isApprovedDocumentStatus(doc))
    ), [documents]);

    const hasApprovedBirthCertificate = useMemo(() => (
        documents.some((doc) => isBirthCertificateDocument(doc) && isApprovedDocumentStatus(doc))
    ), [documents]);

    const documentsValid = useMemo(() => (
        hasApprovedPictureId && hasApprovedBirthCertificate && !pendingDocument
    ), [hasApprovedPictureId, hasApprovedBirthCertificate, pendingDocument]);

    const validStyle = documentsValid ? "primary" : "warning";

    const {
        currentData: transitionStatus,
        isFetching: isFetchingTransitionStatus,
        refetch: refetchTransitionStatus,
    } = useGetTroveNextProfileTransitionStatusQuery(
        { profileId: viewProfileId },
        { skip: !viewProfileId || !showProfileModal }
    );

    const transition = useMemo(() => normalizeObject(transitionStatus) ?? null, [transitionStatus]);
    const transitionState = useMemo(() => (
        transition?.status
        || transition?.state
        || transition?.transitionStatus
        || transition?.progressStatus
        || transition?.result
        || null
    ), [transition]);

    const canTransition = useMemo(() => {
        if (!viewProfile) return false;
        const status = transitionState ?? viewProfile?.status ?? viewProfile?.state;
        const normalized = normalizeStatus(status);
        if (normalized === "TRANSITIONED") return false;
        return !isCompletedStatus(status);
    }, [transitionState, viewProfile]);
    const canConfirmUser = useMemo(() => (
        !!viewProfileId && documentsValid
    ), [documentsValid, viewProfileId]);

    const [approveDocument, { isLoading: isApproving }] = useApproveTroveNextProfileDocumentMutation();
    const [rejectDocument, { isLoading: isRejecting }] = useRejectTroveNextProfileDocumentMutation();
    const [resetDocument, { isLoading: isResetting }] = useResetTroveNextProfileDocumentMutation();

    const handleApprove = async (documentId) => {
        try {
            await approveDocument({ documentId }).unwrap();
            cogoToast.success("Document approved");
            refetchDocuments();
            refetchTransitionStatus();
        } catch (e) {
            // Previously swallowed silently - a backend refusal (e.g. rejecting the
            // approve call) looked identical to the button doing nothing at all.
            cogoToast.error(errorMessage(e));
        }
    };

    const handleReject = async (values) => {
        try {
            await rejectDocument({
                documentId: values?.documentId,
                body: {
                    rejectionReason: values?.rejectionReason,
                },
            }).unwrap();
            cogoToast.success("Document rejected");
            refetchDocuments();
            refetchTransitionStatus();
        } catch (e) {
            cogoToast.error(errorMessage(e));
        }
    };

    const handleReset = async (values) => {
        try {
            await resetDocument({
                documentId: values?.documentId,
                body: {
                    ...values,
                    type: getNormalizedDocumentType(values),
                    idType: getNormalizedDocumentIdType(values),
                },
            }).unwrap();
            cogoToast.success("Document reset to pending");
            refetchDocuments();
            refetchTransitionStatus();
        } catch (e) {
            cogoToast.error(errorMessage(e));
        }
    };

    useEffect(() => {
        if (showProfileModal && viewProfileId) {
            refetchDocuments();
            refetchTransitionStatus();
        }
    }, [refetchDocuments, refetchTransitionStatus, showProfileModal, viewProfileId]);

    useEffect(() => {
        if (!showProfileModal) return;

        const firstDocumentKey = documents.length ? `${getDocumentId(documents[0]) || 0}` : null;
        if (!firstDocumentKey) return;

        const hasActiveDocument = documents.some((doc, idx) => `${getDocumentId(doc) || idx}` === activeDocumentKey);
        if (!activeDocumentKey || !hasActiveDocument) {
            setActiveDocumentKey(firstDocumentKey);
        }
    }, [activeDocumentKey, documents, showProfileModal]);

    return (
        <div className="py-4 px-6">
            {isPendingStatus(normalizedStatusFilter) && (
                <FilterGroup.DateRange className={"mb-5"} />
            )}
            <Card>
                <CardHeader title="Trove Next Profiles" />
                <CardBody>
                    <div className="d-flex align-items-center mb-4">
                        <Button
                            type="button"
                            className="btn btn-light btn-hover-primary"
                            onClick={() => refetchProfiles()}
                            disabled={isFetchingProfiles}
                        >
                            Refresh
                        </Button>
                        <div className="ml-4 text-muted">
                            Showing {selectedProfiles.length}{(totalProfilesCount ?? approxTotalCount) != null ? ` of ${totalProfilesCount ?? approxTotalCount}` : ""} record{selectedProfiles.length === 1 ? "" : "s"}
                        </div>
                        <div className="ml-auto" style={{ maxWidth: 260 }}>
                            <div className="input-icon">
                                <input
                                    type="search"
                                    placeholder="Search by name, username or email"
                                    className="form-control"
                                    value={profilesQuery}
                                    onChange={(e) => {
                                        setProfilesQuery(e.target.value);
                                        setProfilesPg(1);
                                    }}
                                />
                                {isFetchingProfiles && (
                                    <span>
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="sr-only">Searching...</span>
                                        </div>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <Table responsive className="table">
                        <thead className="text-muted">
                            <tr>
                                <th className="border-top-0">#</th>
                                <th className="border-top-0">NAME</th>
                                <th className="border-top-0">CUSTODIAN</th>
                                <th className="border-top-0">STATUS</th>
                                <th className="border-top-0" />
                            </tr>
                        </thead>
                        <tbody>
                            {isFetchingProfiles && profiles.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center p-4">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredProfiles.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center p-4 text-muted">
                                        No {profilesQuery.trim() === "" ? "Record" : "Match"} Found
                                    </td>
                                </tr>
                            ) : (
                                filteredProfiles.map((row, idx) => {
                                    const profileId = getProfileId(row);
                                    const userId = getUserId(row);
                                    const number = (profilesPg - 1) * profilesRowPerPage + idx + 1;
                                    return (
                                        <tr key={profileId || number}>
                                            <td style={{ verticalAlign: "middle" }}>
                                                <span className="text-muted">{number}</span>
                                            </td>
                                            <td style={{ verticalAlign: "middle" }}>
                                                {normalizedStatusFilter === "TRANSITIONED" && userId ? (
                                                    <a
                                                        href={`/users/view/${userId}`}
                                                        className="text-dark-75 text-hover-primary mb-1 font-size-lg"
                                                    >
                                                        {getProfileName(row)}
                                                    </a>
                                                ) : (
                                                    <span>{getProfileName(row)}</span>
                                                )}
                                            </td>
                                            <td style={{ verticalAlign: "middle" }}>
                                                {getCustodianUserId(row) ? (
                                                    <a
                                                        href={`/users/view/${getCustodianUserId(row)}`}
                                                        className="text-dark-75 font-weight-bolder text-hover-primary"
                                                    >
                                                        {asText(getCustodianName(row))}
                                                    </a>
                                                ) : (
                                                    asText(getCustodianName(row))
                                                )}
                                            </td>
                                            <td style={{ verticalAlign: "middle" }}>
                                                {renderTroveNextStatus(getTroveNextProfileStatus(row) || "-")}
                                            </td>
                                            <td style={{ verticalAlign: "middle" }}>
                                                <span style={{ whiteSpace: "nowrap" }}>
                                                    <Button
                                                        title="View"
                                                        className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                                                        onClick={() => profileId && openProfileModal(row)}
                                                        disabled={!profileId}
                                                    >
                                                        <span className="svg-icon svg-icon-md svg-icon-primary">
                                                            <ViewIcon />
                                                        </span>
                                                    </Button>
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </Table>

                    <div className="d-flex align-items-center justify-content-between">
                        <div className="text-muted">
                            Showing {filteredProfiles.length}{(totalProfilesCount ?? approxTotalCount) != null ? ` of ${totalProfilesCount ?? approxTotalCount}` : ""} record{filteredProfiles.length === 1 ? "" : "s"} on this page
                        </div>
                        <div className="d-flex align-items-center">
                            <Button
                                type="button"
                                className="btn btn-sm btn-light mr-2"
                                disabled={profilesPg <= 1 || isFetchingProfiles}
                                onClick={() => setProfilesPg((p) => Math.max(1, p - 1))}
                            >
                                Prev
                            </Button>
                            <div className="text-muted" style={{ minWidth: 80, textAlign: "center" }}>
                                Page {profilesPg}
                            </div>
                            <Button
                                type="button"
                                className="btn btn-sm btn-light ml-2"
                                disabled={!hasNextProfilesPage || isFetchingProfiles}
                                onClick={() => setProfilesPg((p) => p + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </CardBody>
            </Card>

            <Modal
                show={showProfileModal}
                size={"xl"}
                onHide={closeProfileModal}
                centered
                backdrop={"static"}
                className="h-100"
            >
                <Modal.Header closeButton={false}>
                    <Modal.Title>
                        {viewProfile ? getProfileName(viewProfile) : "Trove Next Profile"}
                    </Modal.Title>
                    <button type="button" className="close" onClick={closeProfileModal}>
                        <span aria-hidden="true" className="d-block">×</span>
                        <span className="sr-only">Close</span>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <Tab.Container
                        id="trove-next-profile-view"
                        activeKey={activeProfileTab}
                        onSelect={(eventKey) => setActiveProfileTab(eventKey || "documents")}
                    >
                        <Row style={{ height: "calc(100vh - 220px)" }}>
                            <Col sm={3} className={"d-flex flex-column border-right h-100"}>
                                <div className={"flex-fill overflow-auto"}>
                                    <Nav className="flex-column">
                                        <Nav.Item>
                                            <Nav.Link eventKey="profile">Profile Details</Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="documents">Documents</Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                </div>
                                {isPendingStatus(normalizedStatusFilter) && (
                                    <div className={"w-100 position-sticky bottom-0 zindex-1 pt-3 bg-white"}>
                                        <Button
                                            type="button"
                                            variant={canConfirmUser ? "success" : "dark"}
                                            className="w-100"
                                            style={canConfirmUser ? {
                                                backgroundColor: "#22A455",
                                                borderColor: "#22A455",
                                                color: "#FFFFFF",
                                                backgroundImage: "none",
                                            } : undefined}
                                            onClick={() => openProfileActionModal(viewProfile, "activate")}
                                            disabled={!canConfirmUser || isActivatingProfile}
                                        >
                                            Confirm User
                                        </Button>
                                    </div>
                                )}
                                {isPendingTransitionAdminStatus(normalizedStatusFilter) && (
                                    <div className={"w-100 position-sticky bottom-0 zindex-1 pt-3 bg-white"}>
                                        <Button
                                            type="button"
                                            variant={"success"}
                                            className="w-100"
                                            style={{
                                                backgroundColor: "#22A455",
                                                borderColor: "#22A455",
                                                color: "#FFFFFF",
                                                backgroundImage: "none",
                                            }}
                                            onClick={() => openProfileActionModal(viewProfile, "transition")}
                                            disabled={!viewProfileId || !canTransition || isApprovingTransition}
                                        >
                                            Transition
                                        </Button>
                                    </div>
                                )}
                            </Col>
                            <Col sm={9} className={"overflow-auto h-100"}>
                                <Tab.Content className={"h-100"}>
                                    <Tab.Pane eventKey="profile" className={"h-100"}>
                                        <Card className="h-100">
                                            <CardHeader title="Profile Details" />
                                            <CardBody>
                                                {!viewProfile ? (
                                                    <div className="text-muted">No profile selected.</div>
                                                ) : (
                                                    <TroveNextBasicDetailsLayout
                                                        profile={viewProfile}
                                                        transition={transition}
                                                        isFetchingTransitionStatus={isFetchingTransitionStatus}
                                                        onSaved={(updatedFields) => {
                                                            // The table refetches on its own via the mutation's
                                                            // "Trove Next" tag invalidation, but this modal holds
                                                            // its own local snapshot of the profile (set once,
                                                            // on open) - merge the saved fields in directly so
                                                            // the form doesn't appear to revert after a save.
                                                            setViewProfile((prev) => (prev ? { ...prev, ...updatedFields } : prev));
                                                            refetchProfiles();
                                                        }}
                                                    />
                                                )}
                                            </CardBody>
                                        </Card>
                                    </Tab.Pane>
                                    <Tab.Pane eventKey="documents" className={"h-100"}>
                                        {/* No Card/CardHeader here on purpose - that wrapper's header bar put the
                                            title on its own bordered row, pushed above the document image instead
                                            of level with it. Pending KYC's own Documents section (see
                                            modal/sections/documents/index.js) renders the "Documents (N)" title as
                                            a plain heading inside the left column instead, so it sits on the same
                                            row as the image and there's no divider under it - matched here. */}
                                        {!viewProfileId ? (
                                            <div className="text-muted">No profile selected.</div>
                                        ) : documents.length === 0 ? (
                                            <div className="text-muted">
                                                {isFetchingDocuments ? "Loading documents..." : "No documents found"}
                                            </div>
                                        ) : (
                                            <Tab.Container
                                                id="trove-next-documents"
                                                activeKey={activeDocumentKey}
                                                onSelect={(eventKey) => setActiveDocumentKey(eventKey || null)}
                                                className={"h-100"}
                                            >
                                                <Row className={"h-100"} style={{ minHeight: 0 }}>
                                                    <Col
                                                        sm={4}
                                                        className={"d-flex flex-column border-right h-100 overflow-auto"}
                                                        style={{ minHeight: 0 }}
                                                    >
                                                        <h3 className="font-weight-bolder">{`Documents (${documents.length})`}</h3>
                                                        <div className={`p-3 mb-3 rounded-sm text-${validStyle} bg-light-${validStyle}`}>
                                                            {documentsValid
                                                                ? "A picture ID and birth certificate are approved and no documents are pending."
                                                                : "A picture ID (NIN/passport) and birth certificate must be approved, with no documents left pending."}
                                                        </div>
                                                        <Nav>
                                                            {documents.map((d, idx) => {
                                                                const documentId = getDocumentId(d);
                                                                return (
                                                                    <DocumentTab
                                                                        key={`${documentId || idx}`}
                                                                        dataKey={documentId || `${idx}`}
                                                                        status={d?.status || d?.state}
                                                                        src={getDocumentImageSrc(d)}
                                                                        title={prettyEnum(getDocumentType(d) || "DOCUMENT").toLowerCase()}
                                                                    />
                                                                );
                                                            })}
                                                        </Nav>
                                                    </Col>
                                                    <Col sm={8} className={"h-100 overflow-auto"} style={{ minHeight: 0 }}>
                                                        <Tab.Content className={"h-100"} style={{ minHeight: 0 }}>
                                                            {documents.map((d, idx) => {
                                                                const documentId = getDocumentId(d);
                                                                return (
                                                                    <Tab.Pane
                                                                        key={`${documentId || idx}`}
                                                                        eventKey={documentId || `${idx}`}
                                                                        className={"h-100"}
                                                                        style={{ minHeight: 0 }}
                                                                    >
                                                                        <TroveNextDocument
                                                                            initialValues={d}
                                                                            isEditable={true}
                                                                            onApprove={handleApprove}
                                                                            onReject={handleReject}
                                                                            onReset={handleReset}
                                                                            isApproving={isApproving}
                                                                            isRejecting={isRejecting}
                                                                            isResetting={isResetting}
                                                                        />
                                                                    </Tab.Pane>
                                                                );
                                                            })}
                                                        </Tab.Content>
                                                    </Col>
                                                </Row>
                                            </Tab.Container>
                                        )}
                                    </Tab.Pane>
                                </Tab.Content>
                            </Col>
                        </Row>
                    </Tab.Container>
                </Modal.Body>
                <TroveNextTransitionModal
                    show={showTransitionModal}
                    onHide={closeTransitionModal}
                    profile={transitionProfile}
                    onConfirm={confirmTransition}
                    title={profileActionType === "activate" ? "Confirm User" : "Transition User"}
                    message={profileActionType === "activate"
                        ? "Are you sure you want to confirm this user?"
                        : "Are you sure you want to transition this user?"}
                    confirmLabel={profileActionType === "activate" ? "Confirm User" : "Transition"}
                    isLoading={profileActionType === "activate" ? isActivatingProfile : isApprovingTransition}
                />
            </Modal>
        </div>
    );
};

class TroveNextErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    render() {
        if (this.state.error) {
            return (
                <div className="p-6">
                    <div className="alert alert-danger">
                        Failed to load Trove Next.
                        <div className="mt-2" style={{ whiteSpace: "pre-wrap" }}>
                            {`${this.state.error?.message || this.state.error}`}
                        </div>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

export default function TroveNextPendingKycWithBoundary(props) {
    return (
        <TroveNextErrorBoundary>
            <FilterGroup key={`trove-next-${props?.userGroup}-${props?.statusFilter}-${props?.documentStatusOverride || ""}-${props?.noDocument ? "no-doc" : ""}`}>
                <FilterGroup.Content>
                    {({ filters }) => <TroveNextPendingKyc {...props} filters={filters} />}
                </FilterGroup.Content>
            </FilterGroup>
        </TroveNextErrorBoundary>
    );
}
